/**
 * Options-drawer filter models. The backend evaluates them (see the calendar filters API guide):
 * values inside one list are OR-ed, different filters are AND-ed. These types only describe what
 * the user picked — the app turns them into query parameters and renders the filtered response.
 */

export interface CalendarDayFilters {
  /** `all` or one of the DAY_QUICK_FILTERS ids. */
  quick: string;
  /** Un-ticked status ids across the booking / confirmation / visit axes. */
  hiddenStatuses: ReadonlySet<string>;
  /** Ticked provider column ids. Empty = every provider. */
  providers: ReadonlySet<string>;
  /** Ticked operatory column ids. Empty = every operatory. */
  operatories: ReadonlySet<string>;
  /** Ticked flags — an appointment must carry at least one. Empty = no flag filter. */
  flags: ReadonlySet<string>;
  /** Minimum appointment production in whole dollars. 0 = all. */
  minProduction: number;
}

export const EMPTY_DAY_FILTERS: CalendarDayFilters = {
  quick: 'all',
  hiddenStatuses: new Set(),
  providers: new Set(),
  operatories: new Set(),
  flags: new Set(),
  minProduction: 0,
};

export type CalendarDayFilterSection =
  | 'quick'
  | 'status'
  | 'providers'
  | 'operatories'
  | 'flags'
  | 'production';

export interface CalendarFilterChip<Id extends string = string> {
  id: Id;
  label: string;
}

const DAY_QUICK_LABELS: Record<string, string> = {
  red: 'Needs attention',
  unconfirmed: 'Unconfirmed',
  notready: 'Not ready',
  building: 'In the building',
  newpt: 'New patients',
};

/** One removable chip per active section — feeds the filter bar and the Options badge. */
export function describeDayFilters(
  filters: CalendarDayFilters,
): CalendarFilterChip<CalendarDayFilterSection>[] {
  const chips: CalendarFilterChip<CalendarDayFilterSection>[] = [];
  if (filters.quick !== 'all') {
    chips.push({ id: 'quick', label: DAY_QUICK_LABELS[filters.quick] ?? filters.quick });
  }
  if (filters.hiddenStatuses.size > 0) {
    chips.push({
      id: 'status',
      label: `Status: ${String(filters.hiddenStatuses.size)} hidden`,
    });
  }
  if (filters.providers.size > 0) {
    chips.push({
      id: 'providers',
      label: `Providers: ${String(filters.providers.size)} selected`,
    });
  }
  if (filters.operatories.size > 0) {
    chips.push({
      id: 'operatories',
      label: `Operatories: ${String(filters.operatories.size)} selected`,
    });
  }
  if (filters.flags.size > 0) {
    chips.push({ id: 'flags', label: `Flags: ${String(filters.flags.size)} selected` });
  }
  if (filters.minProduction > 0) {
    chips.push({
      id: 'production',
      label: `Production ≥ $${filters.minProduction.toLocaleString()}`,
    });
  }
  return chips;
}

// ── Week ────────────────────────────────────────────────────────────────────

export type CalendarWeekStatusId = 'confirmed' | 'pending' | 'no_show' | 'in_treatment';

/** Week drawer checkboxes and the status-axis values each one stands for. */
export const WEEK_STATUS_OPTIONS: readonly {
  id: CalendarWeekStatusId;
  label: string;
  dot: string;
  axis: 'confirmation' | 'visit';
  values: readonly string[];
}[] = [
  {
    id: 'confirmed',
    label: 'Confirmed',
    dot: 'var(--success)',
    axis: 'confirmation',
    values: ['CONFIRMED'],
  },
  {
    id: 'pending',
    label: 'Pending',
    dot: 'var(--gold)',
    axis: 'confirmation',
    values: ['UNCONFIRMED', 'ATTEMPTED'],
  },
  {
    id: 'no_show',
    label: 'No show',
    dot: 'var(--terracotta)',
    axis: 'visit',
    values: ['NO_SHOW'],
  },
  {
    id: 'in_treatment',
    label: 'In treatment',
    dot: 'var(--ai-3)',
    axis: 'visit',
    values: ['SEATED', 'TREATMENT_DONE'],
  },
];

export interface CalendarWeekFilters {
  /** `all` | `yr` (yellow & red) | `red`. */
  readiness: string;
  hiddenStatuses: ReadonlySet<string>;
  /** Window length in days. */
  rangeDays: 7 | 14;
}

export const EMPTY_WEEK_FILTERS: CalendarWeekFilters = {
  readiness: 'all',
  hiddenStatuses: new Set(),
  rangeDays: 7,
};

export type CalendarWeekFilterSection = 'readiness' | 'status' | 'range';

const WEEK_READINESS_LABELS: Record<string, string> = {
  yr: 'Yellow & Red',
  red: 'Red only',
};

export function describeWeekFilters(
  filters: CalendarWeekFilters,
): CalendarFilterChip<CalendarWeekFilterSection>[] {
  const chips: CalendarFilterChip<CalendarWeekFilterSection>[] = [];
  if (filters.rangeDays !== 7) {
    chips.push({ id: 'range', label: `Next ${String(filters.rangeDays)} days` });
  }
  if (filters.readiness !== 'all') {
    chips.push({
      id: 'readiness',
      label: WEEK_READINESS_LABELS[filters.readiness] ?? filters.readiness,
    });
  }
  if (filters.hiddenStatuses.size > 0) {
    chips.push({ id: 'status', label: `Status: ${String(filters.hiddenStatuses.size)} hidden` });
  }
  return chips;
}

// ── Month ───────────────────────────────────────────────────────────────────

export type CalendarMonthHeatmap = 'goal' | 'new' | 'noshow';

export type CalendarMonthCellPart = 'production' | 'goal' | 'newpt' | 'risk';

export const MONTH_CELL_PART_OPTIONS: readonly {
  id: CalendarMonthCellPart;
  label: string;
  dot: string;
}[] = [
  { id: 'production', label: 'Production total', dot: 'var(--success)' },
  { id: 'goal', label: 'Goal attainment', dot: 'var(--gold)' },
  { id: 'newpt', label: 'New-patient badge', dot: 'var(--ai-3)' },
  { id: 'risk', label: 'Risk flags', dot: 'var(--terracotta)' },
];

export const MONTH_HEATMAP_OPTIONS: readonly { id: CalendarMonthHeatmap; label: string }[] = [
  { id: 'goal', label: 'Goal %' },
  { id: 'new', label: 'New patients' },
  { id: 'noshow', label: 'No-shows' },
];

export interface CalendarMonthFilters {
  /** `all` | `under` | `issues`. */
  highlight: string;
  heatmap: CalendarMonthHeatmap;
  hiddenCellParts: ReadonlySet<string>;
}

export const EMPTY_MONTH_FILTERS: CalendarMonthFilters = {
  highlight: 'all',
  heatmap: 'goal',
  hiddenCellParts: new Set(),
};

export type CalendarMonthFilterSection = 'highlight' | 'heatmap' | 'cells';

const MONTH_HIGHLIGHT_LABELS: Record<string, string> = {
  under: 'Under goal',
  issues: 'Issues only',
};

export function describeMonthFilters(
  filters: CalendarMonthFilters,
): CalendarFilterChip<CalendarMonthFilterSection>[] {
  const chips: CalendarFilterChip<CalendarMonthFilterSection>[] = [];
  if (filters.highlight !== 'all') {
    chips.push({
      id: 'highlight',
      label: MONTH_HIGHLIGHT_LABELS[filters.highlight] ?? filters.highlight,
    });
  }
  if (filters.heatmap !== 'goal') {
    const label = MONTH_HEATMAP_OPTIONS.find((o) => o.id === filters.heatmap)?.label ?? '';
    chips.push({ id: 'heatmap', label: `Heatmap: ${label}` });
  }
  if (filters.hiddenCellParts.size > 0) {
    chips.push({ id: 'cells', label: `Cell details: ${String(filters.hiddenCellParts.size)} hidden` });
  }
  return chips;
}
